import { computed, defineComponent, onMounted, reactive, watch } from 'vue';
import { useStore } from 'vuex';
import { FiscalYearAction, FiscalYearGetter } from '@/store/fiscalYear/fiscalYear.module';
import { FiscalYear } from '@/store/fiscalYear/fiscalYear.model';
import { ProjectTypeAction, ProjectTypeGetter } from '@/store/projectType/projectType.module';
import { ProjectType } from '@/store/projectType/projectType.model';
import { CabinetAction, CabinetGetter } from '@/store/cabinet/cabinet.module';
import { Cabinet } from '@/store/cabinet/cabinet.model';
import { CabinetMinisterAction, CabinetMinisterGetter } from '@/store/cabinetMinister/cabinetMinister.module';
import { CabinetMinister } from '@/store/cabinetMinister/cabinetMinister.model';
import { ProvinceAction, ProvinceGetter } from '@/store/province/province.module';
import { Province } from '@/store/province/province.model';
import { ProjectSearch } from '@/store/project/project.model';
import { BudgetTypeAction, BudgetTypeGetter } from '@/store/budgetType/budgetType.module';
import { BudgetType } from '@/store/budgetType/budgetType.model';
import VueSelect from 'vue-select';

export default defineComponent({
  name: 'ProjectFilterComponent',
  components: {
    VueSelect,
  },
  props: {
    mode: {
      type: String
    },
    userId: {
      type: String
    },
    keyword: {
      type: String
    }
  },
  emits: [
    'submit'
  ],
  setup: (props, context) => {
    const store = useStore();
    const fiscalYears = computed<FiscalYear[]>(() => (store.getters[FiscalYearGetter.ALL] as FiscalYear[]).filter(m => m.status <= 0));
    const projectTypes = computed<ProjectType[]>(() => store.getters[ProjectTypeGetter.ALL]);
    const budgetTypes = computed<BudgetType[]>(() => store.getters[BudgetTypeGetter.ALL]);
    const cabinets = computed<Cabinet[]>(() => store.getters[CabinetGetter.BY_FISCAL_YEAR]);
    const cabinetOptions = computed<Array<{ id: number, name: string }>>(() => cabinets.value.map(m => ({ id: m.id, name: cabinetLabel(m) })));
    const cabinetMinisters = computed<Array<{ id: number, name: string }>>(() => {
      const ministers = store.getters[CabinetMinisterGetter.ALL] as CabinetMinister[];
      return ministers.map(m => ({ id: m.id, name: m.minister.fullName }));
    });
    const provinces = computed<Province[]>(() => {
      const all = store.getters[ProvinceGetter.ALL] as Province[];
      return all.filter(m => m.iso !== 'TH-10');
    });
    const projectStatuses = [
      { key: 0, name: 'ยกเลิก' },
      { key: 1, name: 'อนุมัติโครงการ' },
      { key: 2, name: 'จัดหา/จัดซื้อ/จัดจ้าง' },
      { key: 3, name: 'กำลังดำเนินการ' },
      { key: 4, name: 'อยู่ระหว่างการเบิกจ่าย' },
      { key: 5, name: 'เบิกจ่ายแล้วเสร็จ' },
      { key: 6, name: 'จบโครงการ' }
    ];

    // Form
    const formData = reactive<ProjectSearch>({
      keyword: props.keyword,
      fiscalYearId: null,
      cabinetId: null,
      cabinetMinisterId: null,
      provinceId: null,
      projectTypeId: null,
      budgetTypeId: null,
      projectStatusKey: null,
    });

    onMounted(() => {
      store.dispatch(FiscalYearAction.FETCH)
        .then(() =>  {
          formData.fiscalYearId = fiscalYears.value[0].id;
          store.dispatch(ProjectTypeAction.FETCH);
          store.dispatch(BudgetTypeAction.FETCH);
          fiscalYearChange();
        });
    });

    watch(() => props.keyword, (search) => {
      if (search) {
        formData.keyword = search;
      }
    });

    // Clearing the cabinet (or switching to a different one) always invalidates
    // whichever approver was picked under the previous cabinet - reset it, and
    // only load that cabinet's ministers when a cabinet is actually selected.
    watch(() => formData.cabinetId, (cabinetId) => {
      formData.cabinetMinisterId = null;
      if (cabinetId) {
        store.dispatch(CabinetMinisterAction.FETCH, cabinetId);
      }
    });

    const submit = () => {
      context.emit('submit', formData);
    };

    const fiscalYearChange = () => {
      if (formData.fiscalYearId) {
        // The fiscal year changed, so whichever approver/cabinet was selected
        // no longer applies - always reset here rather than relying on the
        // cabinetId watcher, since cabinetId may not actually change value
        // (e.g. staying null, or resolving to the same single cabinet again).
        formData.cabinetMinisterId = null;
        store.dispatch(CabinetAction.FETCH_BY_FISCAL_YEAR, formData.fiscalYearId)
          .then(() => {
            // Leave the cabinet unselected when there's a real choice to make -
            // a search page should start broad rather than silently narrowing
            // to whichever cabinet happens to be latest. But when the year only
            // ever had one cabinet, there's no selector to pick it from, so
            // select it automatically or its ministers would never load.
            formData.cabinetId = cabinets.value.length === 1 ? cabinets.value[0]?.id : null;
          });

        fetchProvince(props.mode as string, formData.fiscalYearId as number, props.userId as string);
      }
    };

    const cabinetLabel = (cabinet: Cabinet) => {
      const start = `พ.ศ. ${cabinet.startDate.getFullYear() + 543}`;
      const end = cabinet.endDate ? `พ.ศ. ${cabinet.endDate.getFullYear() + 543}` : 'ปัจจุบัน';

      return `${cabinet.name} (${start} - ${end})`;
    };

    const fetchProvince = (mode: string, fiscalYearId: number, userId: string) => {
      switch (mode) {
        case 'MINISTER':
          store.dispatch(ProvinceAction.FETCH_MINISTER, { fiscalYearId: fiscalYearId, userId: userId });
          break;
        case 'REGION':
          store.dispatch(ProvinceAction.FETCH_REGION, { fiscalYearId: fiscalYearId, userId: userId });
          break;
        case 'PROVINCE':
          store.dispatch(ProvinceAction.FETCH_PROVINCE, { fiscalYearId: fiscalYearId, userId: userId });
          break;
        default:
          store.dispatch(ProvinceAction.FETCH);
          break;
      }
    };

    return {
      formData,
      fiscalYears,
      projectTypes,
      budgetTypes,
      projectStatuses,
      cabinets,
      cabinetOptions,
      cabinetMinisters,
      provinces,
      submit,
      fiscalYearChange,
    };
  },
});
